import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';

import { ApiError, apiRequest } from '../lib/api-client';

type Category = {
  id: string;
  name: string;
  isActive: boolean;
};

type Product = {
  id: string;
  sku: string;
  name: string;
  category: Category;
  categoryId: string;
  costPrice: number;
  sellingPrice: number;
  grossProfit: number;
  stock: number;
  minimumStock: number;
  imageUrl: string | null;
  isActive: boolean;
};

type ProductForm = {
  name: string;
  categoryId: string;
  costPrice: string;
  sellingPrice: string;
  stock: string;
  minimumStock: string;
  isActive: boolean;
};

const emptyForm: ProductForm = {
  name: '',
  categoryId: '',
  costPrice: '',
  sellingPrice: '',
  stock: '0',
  minimumStock: '0',
  isActive: true,
};

const productImageTypes = ['image/jpeg', 'image/png', 'image/webp'];
const productImageMaxBytes = 5 * 1024 * 1024;

const money = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0,
});

function ProductPhoto({ product }: { product: Product }) {
  const [failed, setFailed] = useState(false);
  if (!product.imageUrl || failed) {
    return (
      <div className="grid size-12 shrink-0 place-items-center rounded-xl bg-ajisai-50 text-sm font-black text-ajisai-700">
        {product.name.slice(0, 2).toUpperCase()}
      </div>
    );
  }
  return (
    <img
      className="size-12 shrink-0 rounded-xl border border-slate-200 object-cover"
      src={product.imageUrl}
      alt={product.name}
      onError={() => setFailed(true)}
    />
  );
}

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${
        active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
      }`}
    >
      {active ? 'Aktif' : 'Nonaktif'}
    </span>
  );
}

export function Products({ canWrite, outletReady }: { canWrite: boolean; outletReady: boolean }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [editing, setEditing] = useState<Product | null | undefined>(undefined);
  const [form, setForm] = useState<ProductForm>(emptyForm);
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [removeCurrentImage, setRemoveCurrentImage] = useState(false);
  const [imageProgress, setImageProgress] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const limit = 10;

  const grossProfit = useMemo(
    () => Number(form.sellingPrice || 0) - Number(form.costPrice || 0),
    [form.costPrice, form.sellingPrice],
  );

  useEffect(
    () => () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    },
    [imagePreviewUrl],
  );

  async function loadProducts() {
    if (!outletReady) {
      setProducts([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await apiRequest<{
        data: Product[];
        meta: { total: number };
      }>(`/products?search=${encodeURIComponent(query)}&page=${page}&limit=${limit}`);
      setProducts(result.data);
      setTotal(result.meta.total);
      setPageError('');
    } catch (reason) {
      setPageError(reason instanceof ApiError ? reason.message : 'Daftar produk gagal dimuat.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadProducts();
  }, [outletReady, page, query]);

  useEffect(() => {
    if (!outletReady) return;
    apiRequest<{ data: Category[] }>('/categories?limit=100')
      .then((result) => setCategories(result.data.filter((category) => category.isActive)))
      .catch(() => setCategories([]));
  }, [outletReady]);

  function openForm(product: Product | null) {
    setForm(
      product
        ? {
            name: product.name,
            categoryId: product.category.id,
            costPrice: String(product.costPrice),
            sellingPrice: String(product.sellingPrice),
            stock: String(product.stock),
            minimumStock: String(product.minimumStock),
            isActive: product.isActive,
          }
        : { ...emptyForm },
    );
    setEditing(product);
    setImageFile(null);
    setImagePreviewUrl('');
    setRemoveCurrentImage(false);
    setImageProgress('');
    setFormError('');
  }

  function closeForm() {
    if (busy) return;
    setEditing(undefined);
    setImageFile(null);
    setImagePreviewUrl('');
    setRemoveCurrentImage(false);
    setImageProgress('');
    setFormError('');
  }

  function selectImage(file?: File) {
    setFormError('');
    if (!file) return;
    if (!productImageTypes.includes(file.type)) {
      setFormError('Format foto harus JPG, PNG, atau WebP.');
      return;
    }
    if (file.size > productImageMaxBytes) {
      setFormError('Ukuran foto produk maksimal 5 MB.');
      return;
    }
    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
    setRemoveCurrentImage(false);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    const costPrice = Number(form.costPrice);
    const sellingPrice = Number(form.sellingPrice);
    const stock = Number(form.stock);
    const minimumStock = Number(form.minimumStock);
    if (
      !form.categoryId ||
      !Number.isFinite(costPrice) ||
      costPrice < 0 ||
      !Number.isFinite(sellingPrice) ||
      sellingPrice < 0 ||
      !Number.isInteger(stock) ||
      stock < 0 ||
      !Number.isInteger(minimumStock) ||
      minimumStock < 0
    ) {
      setFormError(
        'Pilih kategori dan pastikan harga, stok, serta stok minimum tidak bernilai negatif.',
      );
      return;
    }
    setBusy(true);
    setFormError('');
    try {
      const payload = {
        name: form.name.trim(),
        categoryId: form.categoryId,
        sellingPrice,
        minimumStock,
        isActive: form.isActive,
        ...(!editing ? { costPrice, stock } : {}),
      };
      let saved = await apiRequest<Product>(`/products${editing ? `/${editing.id}` : ''}`, {
        method: editing ? 'PATCH' : 'POST',
        body: JSON.stringify(payload),
      });

      try {
        if (imageFile) {
          setImageProgress('Mengoptimalkan dan mengunggah foto...');
          const body = new FormData();
          body.append('file', imageFile);
          saved = await apiRequest<Product>(`/products/${saved.id}/image`, {
            method: 'POST',
            body,
          });
        } else if (removeCurrentImage && saved.imageUrl) {
          setImageProgress('Menghapus foto...');
          saved = await apiRequest<Product>(`/products/${saved.id}/image`, { method: 'DELETE' });
        }
      } catch (reason) {
        setEditing(saved);
        setImageProgress('');
        setFormError(
          `Produk sudah tersimpan, tetapi foto gagal diproses. ${
            reason instanceof ApiError ? reason.message : 'Silakan coba lagi.'
          }`,
        );
        await loadProducts();
        return;
      }
      setEditing(undefined);
      setImageFile(null);
      setImagePreviewUrl('');
      setRemoveCurrentImage(false);
      setImageProgress('');
      await loadProducts();
    } catch (reason) {
      setFormError(reason instanceof ApiError ? reason.message : 'Produk gagal disimpan.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(product: Product) {
    if (
      !window.confirm(
        `Hapus ${product.name}? Produk akan disembunyikan dari Master Produk. Produk dengan stok lebih dari 0 tidak dapat dihapus.`,
      )
    )
      return;
    setDeletingId(product.id);
    setPageError('');
    try {
      await apiRequest(`/products/${product.id}`, { method: 'DELETE' });
      await loadProducts();
    } catch (reason) {
      setPageError(reason instanceof ApiError ? reason.message : 'Produk gagal dihapus.');
    } finally {
      setDeletingId('');
    }
  }

  if (!outletReady) {
    return (
      <section className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center">
        <h1 className="text-xl font-black text-amber-950">Pilih outlet terlebih dahulu</h1>
        <p className="mt-2 text-sm text-amber-800">
          Master Produk selalu mengikuti outlet aktif agar data antar-outlet tetap terpisah.
        </p>
      </section>
    );
  }

  return (
    <section className="page-shell">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="font-bold text-ajisai-700">Master Data</p>
          <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-950">Produk</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            Kelola barang jadi, harga outlet, stok, foto, dan status produk pada outlet aktif.
          </p>
        </div>
        {canWrite && (
          <button
            className="rounded-xl bg-ajisai-700 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-ajisai-700/20 transition hover:bg-ajisai-900"
            onClick={() => openForm(null)}
          >
            + Tambah Produk
          </button>
        )}
      </div>

      <div className="mt-7 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <form
            className="flex min-w-0 flex-1 gap-2 sm:max-w-xl"
            onSubmit={(event) => {
              event.preventDefault();
              setPage(1);
              setQuery(search.trim());
            }}
          >
            <input
              aria-label="Cari produk"
              className="min-w-0 flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-sm"
              placeholder="Cari nama, SKU, atau kategori"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <button className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold hover:bg-slate-50">
              Cari
            </button>
          </form>
          <p className="text-sm font-semibold text-slate-500">{total} produk</p>
        </div>

        {pageError && (
          <p role="alert" className="m-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">
            {pageError}
          </p>
        )}

        {loading ? (
          <div className="space-y-3 p-5">
            {Array.from({ length: 5 }, (_, index) => (
              <div key={index} className="h-16 animate-pulse rounded-xl bg-slate-100" />
            ))}
          </div>
        ) : products.length ? (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full min-w-[1180px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Produk</th>
                    <th className="px-5 py-3">Kode Produk</th>
                    <th className="px-5 py-3">Kategori</th>
                    <th className="px-5 py-3 text-right">Harga Modal Outlet</th>
                    <th className="px-5 py-3 text-right">Harga jual</th>
                    <th className="px-5 py-3 text-right">Stok</th>
                    <th className="px-5 py-3 text-right">Stok Minimum</th>
                    <th className="px-5 py-3 text-right">Laba kotor</th>
                    <th className="px-5 py-3">Status</th>
                    {canWrite && <th className="px-5 py-3 text-right">Aksi</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-slate-50/70">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <ProductPhoto product={product} />
                          <p className="font-bold text-slate-900">{product.name}</p>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 font-mono text-xs text-slate-600">
                        {product.sku}
                      </td>
                      <td className="px-5 py-4">{product.category.name}</td>
                      <td className="whitespace-nowrap px-5 py-4 text-right">
                        {money.format(product.costPrice)}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-right font-bold">
                        {money.format(product.sellingPrice)}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-right font-bold">
                        {product.stock.toLocaleString('id-ID')}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-right">
                        {product.minimumStock.toLocaleString('id-ID')}
                      </td>
                      <td
                        className={`whitespace-nowrap px-5 py-4 text-right font-bold ${
                          product.grossProfit < 0 ? 'text-red-600' : 'text-emerald-700'
                        }`}
                      >
                        {money.format(product.grossProfit)}
                      </td>
                      <td className="px-5 py-4">
                        <StatusBadge active={product.isActive} />
                      </td>
                      {canWrite && (
                        <td className="whitespace-nowrap px-5 py-4 text-right">
                          <button
                            className="font-bold text-ajisai-700 hover:text-ajisai-900"
                            onClick={() => openForm(product)}
                          >
                            Edit
                          </button>
                          <button
                            className="ml-4 font-bold text-red-600 hover:text-red-800 disabled:opacity-50"
                            disabled={deletingId === product.id}
                            onClick={() => {
                              void remove(product);
                            }}
                          >
                            {deletingId === product.id ? 'Menghapus...' : 'Hapus'}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="divide-y divide-slate-100 md:hidden">
              {products.map((product) => (
                <article key={product.id} className="p-5">
                  <div className="flex items-start gap-3">
                    <ProductPhoto product={product} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h2 className="font-black text-slate-900">{product.name}</h2>
                          <p className="mt-1 break-all font-mono text-xs text-slate-500">
                            {product.sku}
                          </p>
                        </div>
                        <StatusBadge active={product.isActive} />
                      </div>
                      <p className="mt-2 text-sm text-slate-500">{product.category.name}</p>
                    </div>
                  </div>
                  <dl className="mt-4 grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-3 text-xs">
                    <div>
                      <dt className="text-slate-500">Modal outlet</dt>
                      <dd className="mt-1 font-bold">{money.format(product.costPrice)}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Jual</dt>
                      <dd className="mt-1 font-bold">{money.format(product.sellingPrice)}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Laba</dt>
                      <dd
                        className={`mt-1 font-bold ${
                          product.grossProfit < 0 ? 'text-red-600' : 'text-emerald-700'
                        }`}
                      >
                        {money.format(product.grossProfit)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Stok</dt>
                      <dd className="mt-1 font-bold">{product.stock.toLocaleString('id-ID')}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Stok minimum</dt>
                      <dd className="mt-1 font-bold">
                        {product.minimumStock.toLocaleString('id-ID')}
                      </dd>
                    </div>
                  </dl>
                  {canWrite && (
                    <div className="mt-4 flex gap-2">
                      <button
                        className="flex-1 rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-bold"
                        onClick={() => openForm(product)}
                      >
                        Edit
                      </button>
                      <button
                        className="flex-1 rounded-xl border border-red-200 px-3 py-2.5 text-sm font-bold text-red-700"
                        disabled={deletingId === product.id}
                        onClick={() => {
                          void remove(product);
                        }}
                      >
                        {deletingId === product.id ? 'Menghapus...' : 'Hapus'}
                      </button>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </>
        ) : (
          <div className="px-6 py-16 text-center">
            <p className="text-lg font-black text-slate-800">
              {query ? 'Produk tidak ditemukan' : 'Belum ada produk'}
            </p>
            <p className="mt-2 text-sm text-slate-500">
              {query
                ? 'Coba kata pencarian lain.'
                : 'Tambahkan produk pertama untuk mulai menyiapkan katalog outlet.'}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-4 text-sm">
          <p className="text-slate-500">
            Halaman {page} dari {Math.max(1, Math.ceil(total / limit))}
          </p>
          <div className="flex gap-2">
            <button
              className="rounded-lg border px-3 py-2 font-bold disabled:opacity-40"
              disabled={page === 1}
              onClick={() => setPage((value) => value - 1)}
            >
              Sebelumnya
            </button>
            <button
              className="rounded-lg border px-3 py-2 font-bold disabled:opacity-40"
              disabled={page * limit >= total}
              onClick={() => setPage((value) => value + 1)}
            >
              Berikutnya
            </button>
          </div>
        </div>
      </div>

      {editing !== undefined && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-slate-950/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="product-form-title"
        >
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl sm:p-7">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-bold text-ajisai-700">
                  {editing ? 'Perbarui produk' : 'Produk baru'}
                </p>
                <h2 id="product-form-title" className="mt-1 text-2xl font-black text-slate-950">
                  {editing ? editing.name : 'Tambah Produk'}
                </h2>
              </div>
              <button
                aria-label="Tutup"
                className="rounded-lg px-2 py-1 text-xl text-slate-500 hover:bg-slate-100"
                disabled={busy}
                onClick={closeForm}
              >
                ×
              </button>
            </div>

            <form
              className="mt-6 grid gap-5 sm:grid-cols-2"
              onSubmit={(event) => {
                void save(event);
              }}
            >
              <label className="text-sm font-semibold text-slate-700 sm:col-span-2">
                Kode Produk
                <input
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-mono text-sm text-slate-500"
                  value={editing?.sku ?? 'Dibuat otomatis saat produk disimpan'}
                  readOnly
                />
              </label>

              <label className="text-sm font-semibold text-slate-700 sm:col-span-2">
                Nama produk
                <input
                  className={`mt-2 w-full rounded-xl border px-4 py-3 font-normal ${editing ? 'border-slate-200 bg-slate-50 text-slate-500' : 'border-slate-300'}`}
                  value={form.name}
                  maxLength={160}
                  required
                  autoFocus
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </label>

              <label className="text-sm font-semibold text-slate-700 sm:col-span-2">
                Kategori
                <select
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal"
                  value={form.categoryId}
                  required
                  onChange={(event) => setForm({ ...form, categoryId: event.target.value })}
                >
                  <option value="">Pilih kategori</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {!categories.length && (
                  <span className="mt-2 block text-xs font-normal text-amber-700">
                    Buat kategori aktif terlebih dahulu melalui menu Kategori Produk.
                  </span>
                )}
              </label>

              <label className="text-sm font-semibold text-slate-700">
                Harga Modal Outlet
                <input
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal"
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={form.costPrice}
                  required
                  readOnly={Boolean(editing)}
                  onChange={(event) => setForm({ ...form, costPrice: event.target.value })}
                />
              </label>

              <label className="text-sm font-semibold text-slate-700">
                Harga jual
                <input
                  className={`mt-2 w-full rounded-xl border px-4 py-3 font-normal ${editing ? 'border-slate-200 bg-slate-50 text-slate-500' : 'border-slate-300'}`}
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={form.sellingPrice}
                  required
                  onChange={(event) => setForm({ ...form, sellingPrice: event.target.value })}
                />
              </label>

              <label className="text-sm font-semibold text-slate-700">
                Stok
                <input
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal"
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={form.stock}
                  required
                  readOnly={Boolean(editing)}
                  onChange={(event) => setForm({ ...form, stock: event.target.value })}
                />
                {editing && (
                  <span className="mt-2 block text-xs font-normal text-slate-500">
                    Ubah melalui Penerimaan Barang atau Penyesuaian Stok.
                  </span>
                )}
              </label>

              <label className="text-sm font-semibold text-slate-700">
                Stok Minimum
                <input
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal"
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={form.minimumStock}
                  required
                  onChange={(event) => setForm({ ...form, minimumStock: event.target.value })}
                />
              </label>

              <div
                className={`rounded-xl border p-4 sm:col-span-2 ${
                  grossProfit < 0
                    ? 'border-red-200 bg-red-50 text-red-800'
                    : 'border-emerald-200 bg-emerald-50 text-emerald-800'
                }`}
              >
                <p className="text-xs font-bold uppercase tracking-wider">Laba kotor otomatis</p>
                <p className="mt-1 text-xl font-black">{money.format(grossProfit)}</p>
                {grossProfit < 0 && (
                  <p className="mt-1 text-xs">Harga jual lebih rendah dari harga beli.</p>
                )}
              </div>

              <div className="rounded-xl border border-slate-200 p-4 sm:col-span-2">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                  <div className="grid h-48 w-full shrink-0 place-items-center overflow-hidden rounded-xl bg-slate-50 sm:w-48">
                    {imagePreviewUrl || (!removeCurrentImage && editing?.imageUrl) ? (
                      <img
                        className="h-full w-full object-contain"
                        src={imagePreviewUrl || editing?.imageUrl || ''}
                        alt="Preview foto produk"
                      />
                    ) : (
                      <div className="px-6 text-center text-sm text-slate-500">
                        Belum ada foto produk
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-800">Foto produk</p>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      JPG, PNG, atau WebP. Maksimal 5 MB. Foto otomatis diubah ke WebP dan
                      diperkecil untuk web.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <label className="cursor-pointer rounded-xl bg-ajisai-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-ajisai-800">
                        {editing?.imageUrl || imageFile ? 'Ganti Foto' : 'Upload Foto'}
                        <input
                          className="sr-only"
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          disabled={busy}
                          onChange={(event) => {
                            selectImage(event.target.files?.[0]);
                            event.target.value = '';
                          }}
                        />
                      </label>
                      {imageFile && (
                        <button
                          type="button"
                          className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold"
                          disabled={busy}
                          onClick={() => {
                            setImageFile(null);
                            setImagePreviewUrl('');
                          }}
                        >
                          Batalkan Foto Baru
                        </button>
                      )}
                      {editing?.imageUrl && !removeCurrentImage && (
                        <button
                          type="button"
                          className="rounded-xl border border-red-200 px-4 py-2.5 text-sm font-bold text-red-700"
                          disabled={busy}
                          onClick={() => {
                            setImageFile(null);
                            setImagePreviewUrl('');
                            setRemoveCurrentImage(true);
                          }}
                        >
                          Hapus Foto
                        </button>
                      )}
                      {removeCurrentImage && (
                        <button
                          type="button"
                          className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold"
                          disabled={busy}
                          onClick={() => setRemoveCurrentImage(false)}
                        >
                          Batalkan Hapus
                        </button>
                      )}
                    </div>
                    {(imageFile || removeCurrentImage) && (
                      <p className="mt-3 text-xs font-semibold text-amber-700">
                        Perubahan foto diterapkan saat produk disimpan.
                      </p>
                    )}
                    {imageProgress && (
                      <p role="status" className="mt-3 text-sm font-bold text-ajisai-700">
                        {imageProgress}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <label className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 p-4 sm:col-span-2">
                <span>
                  <span className="block text-sm font-bold text-slate-800">Status produk</span>
                  <span className="mt-1 block text-xs text-slate-500">
                    Produk nonaktif tetap tersimpan, tetapi tidak digunakan untuk transaksi baru.
                  </span>
                </span>
                <input
                  className="size-5"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                />
              </label>

              {formError && (
                <p
                  role="alert"
                  className="rounded-xl bg-red-50 p-3 text-sm text-red-700 sm:col-span-2"
                >
                  {formError}
                </p>
              )}

              <div className="flex flex-col-reverse gap-3 sm:col-span-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  className="rounded-xl border border-slate-300 px-5 py-3 font-bold"
                  disabled={busy}
                  onClick={closeForm}
                >
                  Batal
                </button>
                <button
                  disabled={busy || !categories.length}
                  className="rounded-xl bg-ajisai-700 px-5 py-3 font-bold text-white disabled:opacity-50"
                >
                  {busy ? imageProgress || 'Menyimpan...' : 'Simpan Produk'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
